import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Eye, EyeOff, Loader2, Lock } from 'lucide-react';
import useAuthStore from '../stores/authStore';
import useThemeStore from '../stores/themeStore';
import nolagLogoWhite from '../assets/nolag-logo-white.svg';
import nolagLogoBlack from '../assets/nolag-logo-black.svg';

// ?next= as a path inside this app, or null. Not another site ("//host",
// "/\host"), and not /login itself, which would bounce here forever.
function safeNext(search) {
  const next = new URLSearchParams(search).get('next');
  if (!next || !next.startsWith('/') || next.startsWith('//') || next.startsWith('/\\')) return null;
  return next.startsWith('/login') ? null : next;
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, error, clearError, isAuthenticated } = useAuthStore();
  const { theme } = useThemeStore();
  const logoSrc = theme === 'light' ? nolagLogoBlack : nolagLogoWhite;

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // The page the user was trying to reach before being sent here: ProtectedRoute
  // passes it in router state; an expired session's hard redirect
  // (api/client.js) passes it as ?next=. Only paths inside this app are taken.
  const from = location.state?.from?.pathname || safeNext(location.search) || '/dashboard';

  useEffect(() => {
    if (isAuthenticated) {
      // Redirect to the page user was trying to access, or dashboard
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  useEffect(() => {
    clearError();
  }, [username, password, clearError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await login(username, password);
    if (result.success) {
      // Redirect to original page or change-password if required
      navigate(result.mustChangePassword ? '/change-password' : from, { replace: true });
    }
  };

  return (
    <div className="login-page">
      {/* Background Effects */}
      <div className="login-bg">
        <div className="login-bg__gradient login-bg__gradient--1" />
        <div className="login-bg__gradient login-bg__gradient--2" />
      </div>

      <div className="login-container">
        {/* Login Card — logo lives INSIDE the card now, not floating above
            it, matching primus-admin-main's login layout. */}
        <div className="login-card glass-card">
          {/* Logo — real noLag asset (same theme-aware swap as Sidebar.jsx),
              not the old hand-drawn blue/purple mark. The image already
              carries the wordmark, so no separate "Primus" text title. */}
          <div className="login-logo">
            <img src={logoSrc} alt="NOLAG" className="login-logo__img" />
            <span className="login-logo__subtitle">SuperAdmin</span>
          </div>

          <div className="login-card__header">
            <h2 className="login-card__title">Welcome back</h2>
            <p className="login-card__subtitle">Sign in to access your dashboard</p>
          </div>

          {error && (
            <div className="login-error">
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <div className="input-group">
              <label htmlFor="username" className="input-label">Username</label>
              <input
                type="text"
                id="username"
                className="input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                autoComplete="username"
                disabled={isLoading}
              />
            </div>

            <div className="input-group">
              <label htmlFor="password" className="input-label">Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  className="input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="btn btn--primary login-submit"
              disabled={isLoading || !username || !password}
            >
              {isLoading ? (
                <>
                  <Loader2 size={18} className="spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <Lock size={16} />
                  Sign In
                </>
              )}
            </button>
          </form>

          <div className="login-links">
            <Link to="/forgot-password" className="login-forgot">Forgot password?</Link>
          </div>

          <div className="login-footer">
            <span>Authorized personnel only</span>
          </div>
        </div>

        {/* Version */}
        <p className="login-version">NOLAG SuperAdmin v1.0.0</p>
      </div>

      <style>{`
        .login-page {
          /* noLag rebrand: scoped to this page only. No Figma node exists
             for the login screen — logo/wordmark/version text swapped from
             the literal old "Primus" branding (flagged as not-yet-touched
             in the earlier rebrand handoff), background glow recolored
             from blue/purple to brand orange/red, accent scoped for the
             Sign In button. */
          --accent-primary: #FE7B00;
          --accent-primary-rgb: 254, 123, 0;
          --accent-primary-glow: rgba(254, 123, 0, 0.25);
          --accent-primary-subtle: rgba(254, 123, 0, 0.12);
          --accent-gradient: linear-gradient(135deg, #FE7B00 0%, #F7302F 100%);

          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: var(--space-8);
          position: relative;
          overflow: hidden;
        }

        .login-bg {
          position: absolute;
          inset: 0;
          z-index: 0;
        }

        .login-bg__gradient {
          position: absolute;
          border-radius: 50%;
          filter: blur(100px);
          opacity: 0.5;
        }

        .login-bg__gradient--1 {
          width: 600px;
          height: 600px;
          top: -200px;
          right: -100px;
          background: radial-gradient(circle, rgba(254, 123, 0, 0.3) 0%, transparent 70%);
        }

        .login-bg__gradient--2 {
          width: 500px;
          height: 500px;
          bottom: -150px;
          left: -100px;
          background: radial-gradient(circle, rgba(247, 48, 47, 0.25) 0%, transparent 70%);
        }

        .login-container {
          position: relative;
          z-index: 1;
          width: 100%;
          max-width: 400px;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        /* Logo — a single horizontal lockup (mark + "SuperAdmin" side by
           side) instead of stacked with a caption underneath, which just
           left a dead gap between them. */
        .login-logo {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: center;
          gap: var(--space-3);
          margin-bottom: var(--space-8);
        }

        .login-logo__img {
          height: 68px;
          width: auto;
          max-width: 100%;
          display: block;
        }

        .login-logo__subtitle {
          font-size: var(--text-2xl);
          font-weight: 700;
          color: var(--accent-primary);
          letter-spacing: 0.01em;
        }

        /* Login Card */
        .login-card {
          width: 100%;
          padding: var(--space-8);
        }

        .login-card__header {
          text-align: center;
          margin-bottom: var(--space-6);
        }

        .login-card__title {
          font-size: var(--text-xl);
          font-weight: 600;
          margin-bottom: var(--space-2);
        }

        .login-card__subtitle {
          font-size: var(--text-sm);
          color: var(--text-tertiary);
        }

        .login-error {
          padding: var(--space-4);
          background: var(--status-danger-subtle);
          border: 1px solid rgba(239, 68, 68, 0.2);
          border-radius: var(--radius-md);
          color: var(--status-danger);
          font-size: var(--text-sm);
          margin-bottom: var(--space-6);
          text-align: center;
        }

        .login-form {
          display: flex;
          flex-direction: column;
          gap: var(--space-5);
        }

        .password-input-wrapper {
          position: relative;
        }

        .password-input-wrapper .input {
          padding-right: 48px;
        }

        .password-toggle {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          padding: 6px;
          background: transparent;
          border: none;
          color: var(--text-tertiary);
          cursor: pointer;
          border-radius: var(--radius-sm);
          transition: color var(--duration-fast) var(--ease-out);
        }

        .password-toggle:hover {
          color: var(--text-primary);
        }

        .login-submit {
          width: 100%;
          padding: 14px;
          margin-top: var(--space-2);
        }

        .login-links {
          margin-top: var(--space-4);
          text-align: center;
        }

        .login-forgot {
          font-size: var(--text-sm);
          color: var(--text-tertiary);
          text-decoration: none;
          transition: color var(--duration-fast) var(--ease-out);
        }

        .login-forgot:hover {
          color: var(--text-primary);
        }

        .login-footer {
          margin-top: var(--space-6);
          padding-top: var(--space-6);
          border-top: 1px solid var(--divider);
          text-align: center;
          font-size: var(--text-xs);
          color: var(--text-quaternary);
        }

        .login-version {
          margin-top: var(--space-8);
          font-size: var(--text-xs);
          color: var(--text-quaternary);
        }
      `}</style>
    </div>
  );
}
